'use client'

import React, { createContext, use, useCallback, useLayoutEffect, useState } from 'react'

import type { Theme, ThemeContextType } from './types'

import { defaultTheme, getImplicitPreference, resolveClientTheme, themeLocalStorageKey } from './shared'

const initialContext: ThemeContextType = {
  setTheme: () => null,
  theme: undefined,
}

const THEME_COOKIE_MAX_AGE = 60 * 60 * 24 * 400

function setThemeCookieClient(resolved: Theme) {
  if (typeof document === 'undefined') return
  document.cookie = `${themeLocalStorageKey}=${encodeURIComponent(resolved)}; path=/; max-age=${THEME_COOKIE_MAX_AGE}; SameSite=Lax`
}

const ThemeContext = createContext(initialContext)

export const ThemeProvider = ({
  children,
  ssrInitialTheme,
}: {
  children: React.ReactNode
  ssrInitialTheme: Theme
}) => {
  const [theme, setThemeState] = useState<Theme>(ssrInitialTheme)
  const [hasClientSynced, setHasClientSynced] = useState(false)

  if (typeof window !== 'undefined' && !hasClientSynced) {
    setHasClientSynced(true)
    const resolved = resolveClientTheme(ssrInitialTheme)
    if (resolved !== theme) {
      setThemeState(resolved)
    }
  }

  useLayoutEffect(() => {
    if (document.documentElement.getAttribute('data-theme') !== theme) {
      document.documentElement.setAttribute('data-theme', theme)
    }
    setThemeCookieClient(theme)
  }, [theme])

  const setTheme = useCallback((themeToSet: Theme | null) => {
    if (themeToSet === null) {
      window.localStorage.removeItem(themeLocalStorageKey)
      const implicitPreference = getImplicitPreference()

      const resolved = implicitPreference ?? defaultTheme
      document.documentElement.setAttribute('data-theme', resolved)
      setThemeState(resolved)
      setThemeCookieClient(resolved)
    } else {
      setThemeState(themeToSet)
      window.localStorage.setItem(themeLocalStorageKey, themeToSet)
      document.documentElement.setAttribute('data-theme', themeToSet)
      setThemeCookieClient(themeToSet)
    }
  }, [])

  return (
    <ThemeContext.Provider value={{ setTheme, theme }}>{children}</ThemeContext.Provider>
  )
}

export const useTheme = (): ThemeContextType => use(ThemeContext)
